// src/hooks/useTextToSpeech.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import { getTtsObjectUrl, revokeObjectUrl, TtsFormat } from '@/components/audio/services/AbielAudioService';
import { useAbielAudio } from '@/components/audio/AbielAudioContext';
import { DEFAULT_VOICE } from '@/constants/voiceConstants';

export type SpeakOptions = {
  voice?: string;                  // e.g. 'alloy'
  model?: string;                  // e.g. 'gpt-4o-mini-tts'
  format?: TtsFormat;              // 'mp3' | 'wav' | 'opus'
};

export type UseTextToSpeechOptions = {
  defaultVoice?: string;
  defaultModel?: string;
  defaultFormat?: TtsFormat;
  /** If true, keep the last created URL to allow replay without re-fetching. */
  keepLastUrl?: boolean;
  /** Called just before playback starts (after URL is ready). */
  onStart?: (sessionId: string, url: string) => void;
  /** Called when playback is handed off to the audio context. */
  onPlay?: (sessionId: string, url: string) => void;
  /** Called if any error occurs during TTS or playback. */
  onError?: (err: unknown) => void;
};

export type UseTextToSpeech = {
  /** Fetch TTS, create a Blob URL, and play through the Abiel audio context. */
  speak: (sessionId: string, text: string, opts?: SpeakOptions) => Promise<string>;
  /** Stop current session (if supported by the audio context) and clean URL if needed. */
  stop: () => Promise<void> | void;
  /** Last generated URL (Blob). Useful for manual controls/replay. */
  url: string | null;
  /** Whether a TTS request is in-flight. */
  isLoading: boolean;
  /** Last error (stringified) */
  error: string | null;
  /** Whether the environment can autoplay (from the audio context). */
  canAutoplay: boolean;
};

/**
 * Binary-safe TTS hook.
 * - Uses the Edge Function returning raw audio bytes and the binary-safe callFunction wrapper.
 * - Plays via AbielAudioContext (respects iOS autoplay policies).
 * - Manages Blob URL lifecycle; optionally keeps last URL for replays.
 */
export function useTextToSpeech(options: UseTextToSpeechOptions = {}): UseTextToSpeech {
  const {
    defaultVoice = DEFAULT_VOICE,
    defaultModel = 'gpt-4o-mini-tts',
    defaultFormat: format = 'mp3',
    keepLastUrl = true,
    onStart,
    onPlay,
    onError,
  } = options;

  const { play, stop: stopContext, canAutoplay } = useAbielAudio();
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setErr] = useState<string | null>(null);

  const currentUrlRef = useRef<string | null>(null);
  const currentSessionRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (!keepLastUrl) {
        try { revokeObjectUrl(currentUrlRef.current || undefined); } catch {}
        currentUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const speak = useCallback<UseTextToSpeech['speak']>(async (sessionId, text, opts) => {
    setIsLoading(true);
    setErr(null);

    const voice = opts?.voice ?? defaultVoice;
    const model = opts?.model ?? defaultModel;
    const fmt   = opts?.format ?? format;

    try {
      // If we shouldn’t keep the last URL, revoke any previous one now.
      if (!keepLastUrl && currentUrlRef.current) {
        revokeObjectUrl(currentUrlRef.current);
        currentUrlRef.current = null;
      }

      // 1) Fetch & build Blob URL via binary-safe pipeline
      const objectUrl = await getTtsObjectUrl({ text, voice, model, format: fmt });

      // 2) Update local refs/state
      currentUrlRef.current = objectUrl;
      currentSessionRef.current = sessionId;
      if (isMountedRef.current) setUrl(objectUrl);

      // 3) Notify hooks
      try { onStart?.(sessionId, objectUrl); } catch {}

      // 4) Hand off to unified audio context for playback
      await play(objectUrl, sessionId);
      try { onPlay?.(sessionId, objectUrl); } catch {}

      return objectUrl;
    } catch (e: any) {
      const msg = e?.message ? String(e.message) : String(e);
      if (isMountedRef.current) setErr(msg);
      try { onError?.(e); } catch {}
      throw e;
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [defaultVoice, defaultModel, format, keepLastUrl, onStart, onPlay, onError, play]);

  const stop = useCallback<UseTextToSpeech['stop']>(async () => {
    try {
      if (stopContext) {
        await Promise.resolve(stopContext()); // stop function takes no arguments
      }
    } catch {
      // ignore
    } finally {
      if (!keepLastUrl && currentUrlRef.current) {
        revokeObjectUrl(currentUrlRef.current);
        currentUrlRef.current = null;
      }
      if (isMountedRef.current && !keepLastUrl) setUrl(null);
    }
  }, [keepLastUrl, stopContext]);

  return {
    speak,
    stop,
    url,
    isLoading,
    error: error,
    canAutoplay,
  };
}

export default useTextToSpeech;
